/**
 * Customers and suppliers, for the company currently open.
 *
 * The first screen built entirely from the shared design system, and therefore
 * the first real test of it. It is thin on purpose: the table, the badge and the
 * switcher all live in `src/ui/`, and the company filter lives in `partyReader`.
 * What is left here is the part that is genuinely this screen's business — which
 * columns a contact list has, and what an empty one says.
 *
 * The company is named at the top at all times, because every row below belongs
 * to it. A customer list with no visible owner is how one company's customers
 * end up on another company's invoice.
 */
import { useState } from 'react';
import { DataTable } from '@ui/components/DataTable';
import { StatusBadge } from '@ui/components/StatusBadge';
import { Button } from '@ui/components/Button';
import { PageHeader } from '@ui/components/PageHeader';
import { ConfirmDialog } from '@ui/components/Modal';
import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { useParties } from './useParties';
import { useDeactivateParty } from './usePartyWrites';
import { PartyEditorDrawer } from './PartyEditorDrawer';
import type { Column } from '@ui/lib/table';
import type { Party, PartyKind } from '@data/db/types';

const KINDS: ReadonlyArray<{ value: PartyKind | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'CUSTOMER', label: 'Customers' },
  { value: 'SUPPLIER', label: 'Suppliers' },
];

/**
 * The columns, as data rather than JSX.
 *
 * The `value` functions are what the table sorts and searches on, so they must
 * return what the user recognises — not an internal field. A column whose
 * `value` returns a raw enum would sort `CUSTOMER` before `SUPPLIER` and search
 * for "customer" instead of the name.
 */
function columnsFor(onEdit: (p: Party) => void, onDeactivate: (p: Party) => void): Column<Party>[] {
  return [
    { key: 'name', header: 'Name', value: (p) => p.name },
    { key: 'code', header: 'Code', value: (p) => p.code },
    {
      key: 'gstin',
      header: 'GSTIN',
      value: (p) => p.gstin,
      // A contact with no GSTIN is ordinary — an unregistered customer, or a
      // supplier billing without one. Dash, not a blank cell that looks broken.
      render: (p) => p.gstin ?? <span className="text-slate-600">—</span>,
    },
    { key: 'state', header: 'State', value: (p) => p.stateName },
    {
      key: 'kind',
      header: 'Type',
      value: (p) => p.kind,
      render: (p) => <StatusBadge status={p.kind} />,
    },
    {
      key: 'opening',
      header: 'Opening balance',
      align: 'right',
      // Money formatting goes through core, never toFixed. A negative opening
      // balance is money owed TO us, and it must not be hidden behind a minus
      // sign the user has to interpret.
      value: (p) => p.openingBalancePaise,
      render: (p) => formatMoney(asMoney(p.openingBalancePaise)),
    },
    {
      key: 'active',
      header: 'Status',
      value: (p) => (p.isActive ? 'ACTIVE' : 'INACTIVE'),
      render: (p) => <StatusBadge status={p.isActive ? 'ACTIVE' : 'INACTIVE'} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      // Not a searchable value: the column holds buttons, and `value` is what the
      // table's own filter searches over.
      value: () => '',
      render: (p) => (
        <span className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="edit-party"
            className="text-brand-400 underline"
            onClick={() => onEdit(p)}
          >
            Edit
          </button>
          {p.isActive && (
            <button
              type="button"
              data-testid="deactivate-party"
              className="text-red-300 underline"
              onClick={() => onDeactivate(p)}
            >
              Deactivate
            </button>
          )}
        </span>
      ),
    },
  ];
}

/**
 * Which side the route asked for.
 *
 * #/contacts/customers and #/contacts/suppliers are separate destinations, so
 * this is an INITIAL value and not the only control. A user who arrived at
 * 'Suppliers' and switched to 'Everyone' has not been anywhere — the filter still
 * does what it says, and the route did not have to be rewritten underneath them.
 */
export function PartyScreen({ initialKind = 'ALL' }: { initialKind?: PartyKind | 'ALL' }) {
  const [kind, setKind] = useState<PartyKind | 'ALL'>(initialKind);
  // No search state here. `DataTable` owns table filtering and renders its own
  // search box; a second one above the table meant every contact screen showed
  // two search fields, and on a phone they pushed the table itself below the
  // fold. The two were not equivalent either — this one searched name, code and
  // GSTIN only, while the table's searches every column the user can see.
  //
  // `null` = drawer closed. `{...}` = creating or editing that row. A single
  // piece of state rather than two booleans, so "new" and "edit row 7" cannot
  // both be true and leave a blank form on top of a populated one.
  const [editing, setEditing] = useState<Party | null | 'new'>(null);
  const [deactivating, setDeactivating] = useState<Party | null>(null);
  const deactivate = useDeactivateParty();
  const parties = useParties({
    kind: kind === 'ALL' ? undefined : kind,
  });
  const rows = parties.data ?? [];
  // The kind the DRAWER creates. "Everyone" creates a customer, because a supplier
  // added from the all-contacts view is the rarer case and the caller who wants a
  // supplier switches to Suppliers first.
  const createKind: PartyKind = kind === 'SUPPLIER' ? 'SUPPLIER' : 'CUSTOMER';

  return (
    <div className="flex flex-col p-4 text-slate-100">
      <PageHeader
        title={
          kind === 'CUSTOMER' ? 'Customers' : kind === 'SUPPLIER' ? 'Suppliers' : 'Contacts'
        }
        description={
          kind === 'SUPPLIER'
            ? 'Companies you buy from.'
            : 'Companies you sell to. Their name and GSTIN are copied onto each invoice, so changing them here never rewrites an issued invoice.'
        }
        actions={
          <Button data-testid="new-party" onClick={() => setEditing('new')}>
            Add {createKind === 'SUPPLIER' ? 'supplier' : 'customer'}
          </Button>
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <div role="group" aria-label="Filter by type" className="flex gap-1">
              {KINDS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  aria-pressed={kind === k.value}
                  className={
                    kind === k.value
                      ? 'rounded bg-brand-500 px-3 py-1 text-sm text-white'
                      : 'rounded border border-slate-700 px-3 py-1 text-sm text-slate-300 hover:border-slate-500'
                  }
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      {parties.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <DataTable
          rows={rows}
          columns={columnsFor(
            (p) => setEditing(p),
            (p) => setDeactivating(p),
          )}
          rowKey={(p) => p.id}
          caption="contacts"
          emptyMessage={
            // The table owns the query now, so the screen cannot know whether the
            // user has typed one. "No contacts yet" stays accurate either way: a
            // search matching nothing is not a claim that contacts are absent.
            kind === 'ALL'
              ? 'No contacts yet. They arrive with an import, or you can add one.'
              : `No ${kind === 'CUSTOMER' ? 'customers' : 'suppliers'} yet.`
          }
        />
      )}

      <PartyEditorDrawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        kind={editing === 'new' ? createKind : (editing?.kind ?? createKind)}
        party={editing === 'new' ? null : editing}
      />

      <ConfirmDialog
        open={deactivating !== null}
        title={`Deactivate ${deactivating?.name ?? ''}?`}
        message={
          'The customer stays on every invoice they were ever billed on, and keeps their ' +
          'name and GSTIN on those documents. They simply stop appearing in new invoices.'
        }
        confirmLabel="Deactivate"
        destructive
        busy={deactivate.isPending}
        onConfirm={() => {
          if (deactivating) deactivate.mutate(deactivating.id);
          setDeactivating(null);
        }}
        onCancel={() => setDeactivating(null)}
      />
    </div>
  );
}
